'use client';

import { useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams, useNavigate, type NavigateFunction } from 'react-router-dom';
import { parseAsStringLiteral, useQueryState } from 'nuqs';
import { motion, AnimatePresence } from 'framer-motion';
import { ProfileHeader } from './ProfileHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ButtonTabs,
  ButtonTabsContent,
  ButtonTabsList,
  ButtonTabsTrigger,
} from '@/components/ui/button-tabs';
import {
  SegmentedTabs,
  SegmentedTabsContent,
  SegmentedTabsList,
  SegmentedTabsTrigger,
} from '@/components/ui/segmented-tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  Users,
  User,
  Briefcase,
  Network,
  Calendar,
  Heart,
  Building2,
  MapPin,
  ArrowRight,
  Mail,
  Phone,
  Plus,
  Loader2,
  Bookmark,
  Pencil,
  Share2,
  QrCode,
  FileUser,
  Clipboard,
  ArrowLeft,
  X,
  Check,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Feed } from '@/pages/dashboard/components/Feed';
import { toast } from 'sonner';
import axiosInstance from '@/lib/axios';
import { getProfileUrl, type PublicUserWithCommunities } from '@/types/profile';
import type { Business } from '@/types';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { displayBusinessAddressFields, hasBusinessAddressForDisplay } from '@/lib/format-address';
import { formatDisplayPhone, phoneTelHref } from '@/lib/phone-utils';
import { formatBusinessCategoryLabel } from '@/lib/business-category';
import { canShareVCard, shareVCard } from '@/lib/share-vcard';
import { useUser } from '@/hooks/use-user';
import { useCommunity } from '@/hooks/use-community';
import { useDiscardChangesConfirm } from '@/hooks/use-discard-changes-confirm';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { communityIdFromPathname } from '@/lib/community-route';
import { OnboardingAnswersReadOnly } from '@/components/profile/onboarding-answers-read-only';
import type {
  Community,
  FormAnswerValue,
  OnboardingAnswerEntry,
  OnboardingFormConfig,
} from '@/types';
import { useCommunityContext } from '@/providers/community-context';
import {
  PersonalInfoSectionEditor,
  type PersonalInfoSectionEditorRef,
} from '@/components/profile/personal-info-section-editor';
import {
  formatVisibleAddress,
  getFieldVisibility,
  type DateVisibility,
  type ProfileVisibilityField,
} from '@/components/forms/date-visibility';
import { DateVisibilityPicker } from '@/components/forms/date-visibility-select';
import { SocialLinksCircleEditor } from '@/components/social/social-links-circle-editor';
import { cn } from '@/lib/utils';

type ShareMode = 'options' | 'text' | 'vcard';

const escapeVCardValue = (value: string) =>
  value
    .replaceAll('\\', '\\\\')
    .replaceAll(';', '\\;')
    .replaceAll(',', '\\,')
    .replaceAll('\n', '\\n');

const PROFILE_TABS = ['details', 'businesses', 'relations', 'communities', 'bookmarks'] as const;
type ProfileTabId = (typeof PROFILE_TABS)[number];

const PROFILE_TAB_LABELS: Record<ProfileTabId, string> = {
  details: 'Profile',
  businesses: 'Business',
  relations: 'Relations',
  communities: 'Community',
  bookmarks: 'Bookmarks',
};

function ProfileDetailsTabIcon({ className }: { className?: string }) {
  return <User className={cn(className, 'h-6 w-6')} />;
}

function ProfileBusinessTabIcon({ className }: { className?: string }) {
  return <Briefcase className={cn(className, 'h-7 w-7 shrink-0 stroke-[2]')} />;
}

function ProfileRelationsTabIcon({ className }: { className?: string }) {
  return <Network className={cn(className, 'h-7 w-7 shrink-0 stroke-[2]')} />;
}

const PROFILE_TAB_ICONS: Record<ProfileTabId, React.ComponentType<{ className?: string }>> = {
  details: ProfileDetailsTabIcon,
  businesses: ProfileBusinessTabIcon,
  relations: ProfileRelationsTabIcon,
  communities: Users,
  bookmarks: Bookmark,
};

type MutualCommunityMember = PublicUserWithCommunities['communities'][number];

interface UserProfileTabSectionProps {
  defaultTab: ProfileTabId;
  tabOrder: ProfileTabId[];
  user: PublicUserWithCommunities;
  isEditable: boolean;
  navigate: NavigateFunction;
  createBusinessPath: string;
  commonCommunities: MutualCommunityMember[];
  onboardingScopeCommunityId?: string;
  routeCommunitySegment?: string;
  headerSlot?: React.ReactNode;
}

/** Owns tab UI + active tab state so remounting via key resets selection without syncing effects. */
function UserProfileTabSection({
  defaultTab,
  tabOrder,
  user,
  isEditable,
  navigate,
  createBusinessPath,
  commonCommunities,
  onboardingScopeCommunityId,
  routeCommunitySegment,
  headerSlot,
}: UserProfileTabSectionProps) {
  const [tabParam, setTabParam] = useQueryState(
    'tab',
    parseAsStringLiteral(PROFILE_TABS).withOptions({ history: 'replace', clearOnDefault: true })
  );
  const activeTab: ProfileTabId = tabParam && tabOrder.includes(tabParam) ? tabParam : defaultTab;

  const handleTabChange = (value: string) => {
    const next = value as ProfileTabId;
    void setTabParam(next === defaultTab ? null : next);
  };

  const [isLeaveDialogOpen, setIsLeaveDialogOpen] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [isEditingPersonal, setIsEditingPersonal] = useState(false);
  const [isPersonalSaving, setIsPersonalSaving] = useState(false);
  const [isPersonalDirty, setIsPersonalDirty] = useState(false);
  const [detailsPane, setDetailsPane] = useState<'profile' | 'additional'>('profile');
  const personalEditorRef = useRef<PersonalInfoSectionEditorRef>(null);
  const { community } = useCommunityContext();
  const { mutate: mutateUser } = useUser();

  const onboardingCommunities = useMemo(() => {
    const scoped = onboardingScopeCommunityId
      ? (user.communities || []).filter((c) => c.id === onboardingScopeCommunityId)
      : user.communities || [];
    return scoped.filter((raw) => {
      const c = raw as Community & { onboardingForm?: OnboardingFormConfig | null };
      return Boolean(c.onboardingForm?.steps?.length);
    });
  }, [user.communities, onboardingScopeCommunityId]);

  const showAdditionalDetailsPane = onboardingCommunities.length > 0;

  const { requestDiscard: requestDiscardPersonal, dialog: personalDiscardDialog } =
    useDiscardChangesConfirm({
      when: isEditingPersonal && isPersonalDirty,
      onSave: async () => {
        await personalEditorRef.current?.save();
      },
    });
  const { dialog: personalNavGuardDialog } = useUnsavedChangesGuard({
    when: isEditingPersonal && isPersonalDirty,
  });

  const handleLeaveCommunity = async () => {
    const targetCommunityId = community?.id || onboardingScopeCommunityId || routeCommunitySegment;
    if (!targetCommunityId) {
      toast.error('Community ID not found');
      return;
    }

    try {
      setIsLeaving(true);
      await axiosInstance.patch(`/api/communities/${targetCommunityId}/leave`);
      toast.success('Left community successfully');
      setIsLeaveDialogOpen(false);
      await mutateUser();
      navigate('/dashboard', { replace: true });
    } catch (error: unknown) {
      const axiosErr = error as { response?: { data?: { error?: string } } };
      const message = axiosErr.response?.data?.error || 'Failed to leave community';
      toast.error(message);
    } finally {
      setIsLeaving(false);
    }
  };

  return (
    <>
      {personalDiscardDialog}
      {personalNavGuardDialog}
      <ButtonTabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
        <div className="flex justify-center px-1">
          <ButtonTabsList className="w-auto max-w-2xl mx-auto justify-start sm:justify-center overflow-x-auto scrollbar-hide gap-3">
            {tabOrder.map((tid) => (
              <ButtonTabsTrigger
                key={tid}
                value={tid}
                icon={PROFILE_TAB_ICONS[tid]}
                aria-label={PROFILE_TAB_LABELS[tid]}
                title={PROFILE_TAB_LABELS[tid]}
              >
                {PROFILE_TAB_LABELS[tid]}
              </ButtonTabsTrigger>
            ))}
          </ButtonTabsList>
        </div>

        {headerSlot}

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <ButtonTabsContent value="businesses" className="mt-0 space-y-4">
              {isEditable && user.businesses && user.businesses.length > 0 ? (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
                  <Button
                    type="button"
                    className="w-full sm:w-auto gap-2 rounded-xl font-bold shadow-primary/20"
                    onClick={() => navigate(createBusinessPath)}
                  >
                    <Plus className="h-4 w-4" />
                    Create new business
                  </Button>
                </div>
              ) : null}
              {user.businesses && user.businesses.length > 0 ? (
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 list-none p-0 m-0">
                  {user.businesses.map((business: Business) => (
                    <li key={business.id} className="min-w-0 flex">
                      <BusinessCard
                        business={business}
                        communitySegment={routeCommunitySegment}
                        isEditable={Boolean(isEditable)}
                        editPath={`${createBusinessPath}/${business.id}`}
                      />
                    </li>
                  ))}
                </ul>
              ) : (
                <Card className="profile-card border-dashed">
                  <CardContent className="flex flex-col items-center text-center py-14 md:py-16 px-6">
                    <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted/50 dark:bg-muted/30 text-muted-foreground mb-4">
                      <Building2 className="h-7 w-7" aria-hidden />
                    </span>
                    <h3 className="text-lg font-semibold font-display text-foreground">
                      No businesses yet
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2 max-w-sm leading-relaxed">
                      {isEditable
                        ? 'Add a business profile to share it with your communities.'
                        : "This user hasn't added any businesses."}
                    </p>
                    {isEditable ? (
                      <Button
                        type="button"
                        className="mt-6 gap-2 rounded-xl font-bold"
                        onClick={() => navigate(createBusinessPath)}
                      >
                        <Plus className="h-4 w-4" />
                        Create new business
                      </Button>
                    ) : null}
                  </CardContent>
                </Card>
              )}
            </ButtonTabsContent>

            <ButtonTabsContent value="relations" className="mt-0 space-y-6">
              <div>
                <h3 className="text-lg font-black font-display tracking-tight mb-3 text-foreground font-outfit">
                  Friends
                </h3>
                {(() => {
                  const friends = (user.friends || []) as NonNullable<
                    PublicUserWithCommunities['friends']
                  >;
                  if (friends.length === 0) {
                    return (
                      <div className="text-center py-8 bg-muted/20 rounded-2xl border border-dashed border-border/60">
                        <p className="text-sm text-muted-foreground">No friends added yet.</p>
                      </div>
                    );
                  }
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {friends.map((friend) => {
                        const name =
                          `${friend.relative?.firstName || ''} ${friend.relative?.lastName || ''}`.trim() ||
                          'User';
                        const link = routeCommunitySegment
                          ? `/${routeCommunitySegment}/u/${friend.relativeId}`
                          : `/u/${friend.relativeId}`;
                        return (
                          <Link key={friend.id} to={link} className="w-full">
                            <Card className="glass-card hover:shadow-lg transition-all border-border/70 rounded-2xl overflow-hidden group">
                              <CardContent className="p-3 flex items-center gap-3">
                                <Avatar className="h-10 w-10 rounded-xl shadow-sm group-hover:scale-105 transition-transform">
                                  <AvatarImage
                                    src={friend.relative?.image || undefined}
                                    className="object-cover"
                                  />
                                  <AvatarFallback className="bg-primary/10 text-primary font-bold">
                                    {name[0]}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                  <p className="font-bold text-foreground text-sm leading-tight truncate">
                                    {name}
                                  </p>
                                </div>
                                <Badge className="bg-violet-500/15 hover:bg-violet-500/15 text-violet-800 dark:text-violet-300 border border-violet-500/25 dark:border-violet-500/35 font-bold text-[9px] px-2 py-0.5 rounded-full uppercase tracking-wider">
                                  Friend
                                </Badge>
                              </CardContent>
                            </Card>
                          </Link>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              <div>
                <h3 className="text-lg font-black font-display tracking-tight mb-3 text-foreground font-outfit">
                  Family Members
                </h3>
                {(() => {
                  const family = user.familyRelations || [];
                  if (family.length === 0) {
                    return (
                      <div className="text-center py-8 bg-muted/20 rounded-2xl border border-dashed border-border/60">
                        <p className="text-sm text-muted-foreground">
                          No family members added yet.
                        </p>
                      </div>
                    );
                  }
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {family.map((member) => {
                        const name =
                          `${member.relative?.firstName || ''} ${member.relative?.lastName || ''}`.trim() ||
                          'User';
                        const link = routeCommunitySegment
                          ? `/${routeCommunitySegment}/u/${member.relativeId}`
                          : `/u/${member.relativeId}`;
                        return (
                          <Link key={member.id} to={link} className="w-full">
                            <Card className="glass-card hover:shadow-lg transition-all border-border/70 rounded-2xl overflow-hidden group">
                              <CardContent className="p-3 flex items-center gap-3">
                                <Avatar className="h-10 w-10 rounded-xl shadow-sm group-hover:scale-105 transition-transform">
                                  <AvatarImage
                                    src={member.relative?.image || undefined}
                                    className="object-cover"
                                  />
                                  <AvatarFallback className="bg-primary/10 text-primary font-bold">
                                    {name[0]}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                  <p className="font-bold text-foreground text-sm leading-tight truncate">
                                    {name}
                                  </p>
                                </div>
                                <Badge className="bg-primary/15 hover:bg-primary/15 text-primary border border-primary/25 font-bold text-[9px] px-2 py-0.5 rounded-full uppercase tracking-wider">
                                  {member.relationship}
                                </Badge>
                              </CardContent>
                            </Card>
                          </Link>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </ButtonTabsContent>

            <ButtonTabsContent value="communities" className="mt-0 space-y-3">
              <p className="text-center text-xs text-muted-foreground md:text-sm md:text-start">
                Communities you&apos;re both members of — shared memberships between you and{' '}
                {user.firstName}.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {commonCommunities.length > 0 ? (
                  commonCommunities.map((community) => (
                    <Link
                      key={community.id}
                      to={`/${community.id}/dashboard`}
                      className="w-full max-w-sm"
                    >
                      <Card className="glass-card hover:shadow-lg transition-all border-border/70 rounded-2xl overflow-hidden group">
                        <CardContent className="p-3 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-12 w-12 rounded-xl shadow-sm group-hover:scale-105 transition-transform">
                              <AvatarImage src={community.logo} className="object-cover" />
                              <AvatarFallback>
                                <Users className="h-5 w-5 text-primary" />
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-bold text-foreground text-sm leading-tight">
                                {community.name}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-1 justify-end max-w-[100px]">
                            {community.roles.map((role) => (
                              <Badge
                                key={role}
                                variant="secondary"
                                className="text-[8px] font-black uppercase tracking-wider py-0 px-1 bg-primary/5 text-primary border-primary/10"
                              >
                                {role}
                              </Badge>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))
                ) : (
                  <div className="col-span-full text-center py-20 bg-muted/30 rounded-3xl border-2 border-dashed border-border/50 px-4">
                    <Users className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                    <h3 className="text-lg font-bold text-muted-foreground">
                      No mutual communities yet
                    </h3>
                    <p className="text-sm text-muted-foreground/60 mt-1 max-w-md mx-auto">
                      You&apos;re not in any of the same communities as{' '}
                      <span className="font-semibold text-foreground">{user.firstName}</span> yet —
                      when you share a membership, it will appear here.
                    </p>
                  </div>
                )}
              </div>
            </ButtonTabsContent>

            <ButtonTabsContent value="details" className="mt-0">
              <div className="space-y-4">
                {showAdditionalDetailsPane ? (
                  <SegmentedTabs
                    value={detailsPane}
                    onValueChange={(value) => {
                      if (isEditingPersonal && value !== 'profile') {
                        requestDiscardPersonal(() => {
                          setIsEditingPersonal(false);
                          setIsPersonalDirty(false);
                          setDetailsPane(value as 'profile' | 'additional');
                        });
                        return;
                      }
                      setDetailsPane(value as 'profile' | 'additional');
                    }}
                    className="space-y-4"
                  >
                    <div className="flex justify-center px-1">
                      <SegmentedTabsList
                        variant="primary"
                        className="w-full max-w-sm gap-1.5 p-1.5"
                      >
                        <SegmentedTabsTrigger
                          variant="primary"
                          value="profile"
                          className="rounded-lg px-3 py-2 text-xs font-semibold"
                        >
                          Profile details
                        </SegmentedTabsTrigger>
                        <SegmentedTabsTrigger
                          variant="primary"
                          value="additional"
                          className="rounded-lg px-3 py-2 text-xs font-semibold"
                        >
                          Additional details
                        </SegmentedTabsTrigger>
                      </SegmentedTabsList>
                    </div>

                    <SegmentedTabsContent value="profile" className="mt-0">
                      <PersonalContactCard
                        user={user}
                        isEditable={isEditable}
                        isEditingPersonal={isEditingPersonal}
                        isPersonalSaving={isPersonalSaving}
                        personalEditorRef={personalEditorRef}
                        onRequestDiscard={() =>
                          requestDiscardPersonal(() => {
                            setIsEditingPersonal(false);
                            setIsPersonalDirty(false);
                          })
                        }
                        onStartEdit={() => setIsEditingPersonal(true)}
                        onSave={() => void personalEditorRef.current?.save().catch(() => undefined)}
                        onDone={() => {
                          setIsEditingPersonal(false);
                          setIsPersonalDirty(false);
                        }}
                        onSavingChange={setIsPersonalSaving}
                        onDirtyChange={setIsPersonalDirty}
                      />
                    </SegmentedTabsContent>

                    <SegmentedTabsContent value="additional" className="mt-0 space-y-4">
                      {onboardingCommunities.map((raw) => {
                        const c = raw as Community & {
                          metadata?: {
                            onboardingAnswers?: Record<
                              string,
                              FormAnswerValue | OnboardingAnswerEntry
                            >;
                          };
                          onboardingForm?: OnboardingFormConfig | null;
                        };
                        return (
                          <OnboardingAnswersReadOnly
                            key={c.id}
                            communityId={c.id}
                            communityTitle={c.name}
                            onboardingFormConfig={c.onboardingForm}
                            onboardingAnswers={c.metadata?.onboardingAnswers}
                            isEditable={isEditable}
                          />
                        );
                      })}
                    </SegmentedTabsContent>
                  </SegmentedTabs>
                ) : (
                  <PersonalContactCard
                    user={user}
                    isEditable={isEditable}
                    isEditingPersonal={isEditingPersonal}
                    isPersonalSaving={isPersonalSaving}
                    personalEditorRef={personalEditorRef}
                    onRequestDiscard={() =>
                      requestDiscardPersonal(() => {
                        setIsEditingPersonal(false);
                        setIsPersonalDirty(false);
                      })
                    }
                    onStartEdit={() => setIsEditingPersonal(true)}
                    onSave={() => void personalEditorRef.current?.save().catch(() => undefined)}
                    onDone={() => {
                      setIsEditingPersonal(false);
                      setIsPersonalDirty(false);
                    }}
                    onSavingChange={setIsPersonalSaving}
                    onDirtyChange={setIsPersonalDirty}
                  />
                )}

                {isEditable ? (
                  <>
                    <Button
                      className="w-full bg-red-500 hover:bg-red-600 rounded-3xl"
                      onClick={() => setIsLeaveDialogOpen(true)}
                    >
                      Leave Community
                    </Button>

                    <AlertDialog open={isLeaveDialogOpen} onOpenChange={setIsLeaveDialogOpen}>
                      <AlertDialogContent className="rounded-2xl">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="font-display font-bold">
                            Leave Community
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to leave the{' '}
                            <span className="font-bold">{community?.name}</span> community? You may
                            lose access to community-exclusive features and content.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel disabled={isLeaving} className="rounded-xl">
                            Cancel
                          </AlertDialogCancel>
                          <AlertDialogAction
                            disabled={isLeaving}
                            className="bg-red-500 hover:bg-red-600 text-white rounded-xl"
                            onClick={handleLeaveCommunity}
                          >
                            {isLeaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Leave Community
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                ) : null}
              </div>
            </ButtonTabsContent>

            <ButtonTabsContent value="bookmarks" className="mt-0 space-y-4">
              {(() => {
                const targetCommunityId =
                  community?.id || onboardingScopeCommunityId || routeCommunitySegment;

                if (!targetCommunityId) {
                  return (
                    <Card className="profile-card border-dashed">
                      <CardContent className="flex flex-col items-center text-center py-14 px-6">
                        <Bookmark className="h-10 w-10 text-muted-foreground mb-3 opacity-60" />
                        <h3 className="text-lg font-semibold text-foreground">
                          Select a Community
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1 max-w-md">
                          Please view your profile within a community to see your bookmarked posts
                          for that community.
                        </p>
                      </CardContent>
                    </Card>
                  );
                }

                return (
                  <Feed
                    communityId={targetCommunityId}
                    fetchEndpoint={`/api/communities/${targetCommunityId}/posts/bookmarks`}
                    emptyTitle="No Bookmarked Posts"
                    emptySubtitle="Posts you bookmark in this community will appear here."
                  />
                );
              })()}
            </ButtonTabsContent>
          </motion.div>
        </AnimatePresence>
      </ButtonTabs>
    </>
  );
}

interface UserProfileViewProps {
  user: PublicUserWithCommunities;
  isEditable?: boolean;
  onEdit?: () => void;
  /** When set (canonical community id), Details tab onboarding cards are limited to this community. */
  onboardingScopeCommunityId?: string;
}

export function UserProfileView({
  user,
  isEditable,
  onEdit,
  onboardingScopeCommunityId,
}: UserProfileViewProps) {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const { communities: myCommunities } = useUser();

  const routeCommunitySegment = params.communityId ?? communityIdFromPathname(location.pathname);

  const { community: scopedCommunityPkg } = useCommunity(routeCommunitySegment);

  const viewingInBusinessCommunityContext = Boolean(
    routeCommunitySegment && scopedCommunityPkg?.community?.isBusinessEnabled === true
  );

  const showBusinessTab =
    Boolean(isEditable) || (Array.isArray(user.businesses) && user.businesses.length > 0);

  /** Hidden on own-profile view (editable). */
  const showCommunitiesTab = !isEditable;

  const { tabOrder, defaultTab } = useMemo(() => {
    type Plan = { tabOrder: ProfileTabId[]; defaultTab: ProfileTabId };

    if (!showCommunitiesTab) {
      const order: ProfileTabId[] = ['details'];
      if (showBusinessTab) order.push('businesses');
      order.push('relations');
      if (isEditable) order.push('bookmarks');
      return { tabOrder: order, defaultTab: 'details' } satisfies Plan;
    }

    if (viewingInBusinessCommunityContext) {
      const order: ProfileTabId[] = showBusinessTab
        ? ['businesses', 'details', 'relations', 'communities']
        : ['details', 'relations', 'communities'];
      if (isEditable) order.push('bookmarks');
      return {
        tabOrder: order,
        defaultTab: showBusinessTab ? 'businesses' : 'details',
      } satisfies Plan;
    }

    /** Communities always rightmost. */
    const order: ProfileTabId[] = ['details'];
    if (showBusinessTab) order.push('businesses');
    order.push('relations');
    order.push('communities');
    if (isEditable) order.push('bookmarks');

    return { tabOrder: order, defaultTab: 'details' } satisfies Plan;
  }, [showCommunitiesTab, viewingInBusinessCommunityContext, showBusinessTab, isEditable]);

  const tabOrderKey = tabOrder.join(',');

  const tabsIdentityKey = useMemo(
    () =>
      `${user.id}|${routeCommunitySegment ?? '-'}|${
        viewingInBusinessCommunityContext ? 'bizCtx' : 'noBizCtx'
      }|${tabOrderKey}|${showCommunitiesTab ? 'soc' : 'self'}`,
    [
      user.id,
      routeCommunitySegment,
      viewingInBusinessCommunityContext,
      tabOrderKey,
      showCommunitiesTab,
    ]
  );

  const createBusinessPath = scopedCommunityPkg?.community?.id
    ? `/${scopedCommunityPkg.community.id}/create/business`
    : '/create/business';

  const commonCommunities = user.communities.filter((c) =>
    myCommunities.some((myC) => myC.id === c.id)
  );

  return (
    <div className="space-y-4">
      <div className="max-w-5xl mx-auto md:px-6">
        <UserProfileTabSection
          key={tabsIdentityKey}
          defaultTab={defaultTab}
          tabOrder={tabOrder}
          user={user}
          isEditable={Boolean(isEditable)}
          navigate={navigate}
          createBusinessPath={createBusinessPath}
          commonCommunities={commonCommunities}
          onboardingScopeCommunityId={onboardingScopeCommunityId}
          routeCommunitySegment={routeCommunitySegment}
          headerSlot={
            isEditable ? null : (
              <ProfileHeader type="user" user={user} isEditable={isEditable} onEdit={onEdit} />
            )
          }
        />
      </div>
    </div>
  );
}

function BusinessCard({
  business,
  communitySegment,
  isEditable,
  editPath,
}: {
  business: Business;
  communitySegment?: string;
  isEditable?: boolean;
  editPath?: string;
}) {
  const navigate = useNavigate();

  const businessHref = communitySegment
    ? `/${communitySegment}${getProfileUrl({ businessId: business.id })}`
    : getProfileUrl({ businessId: business.id });

  const categoryLabel = formatBusinessCategoryLabel(
    business.category,
    business.subCategory,
    business.selfDefinedCategory
  );
  const locationLabel = hasBusinessAddressForDisplay({
    address: business.address,
    addressJson: business.addressJson,
  })
    ? displayBusinessAddressFields(
        { address: business.address, addressJson: business.addressJson },
        true
      )
    : null;

  const initials = (business.name || 'B').trim().charAt(0).toUpperCase();

  return (
    <motion.div
      whileHover={{ y: -2 }}
      transition={{ duration: 0.2 }}
      className="w-full flex-1 min-w-0"
    >
      <Card
        className="profile-card h-full flex flex-col overflow-hidden group hover:shadow-lg transition-shadow cursor-pointer"
        onClick={() => navigate(businessHref)}
      >
        <CardContent className="flex flex-col flex-1 gap-4 p-4 md:p-5">
          <div className="flex items-start gap-3 md:gap-4 min-w-0">
            <Avatar className="h-12 w-12 md:h-14 md:w-14 rounded-2xl shadow-sm shrink-0 ring-2 ring-primary/10 group-hover:ring-primary/25 transition-all">
              <AvatarImage src={business.logo} alt="" className="object-cover" />
              <AvatarFallback className="rounded-2xl bg-gradient-to-br from-primary/15 to-primary/10 text-primary font-bold text-lg">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1 space-y-2">
              <h3 className="font-display font-semibold text-base md:text-lg text-foreground leading-snug truncate group-hover:text-primary transition-colors">
                {business.name}
              </h3>
              <div className="space-y-1.5">
                {categoryLabel ? (
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5 min-w-0">
                    <Briefcase className="h-3.5 w-3.5 shrink-0 text-primary/80" aria-hidden />
                    <span className="truncate">{categoryLabel}</span>
                  </p>
                ) : null}
                {locationLabel ? (
                  <p className="text-xs text-muted-foreground flex items-start gap-1.5 min-w-0 leading-snug">
                    <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5 text-primary/70" aria-hidden />
                    <span className="line-clamp-2">{locationLabel}</span>
                  </p>
                ) : null}
              </div>
              {business.workArea ? (
                <Badge
                  variant="secondary"
                  className="w-fit bg-primary/10 text-primary border-primary/20 font-semibold text-[10px] uppercase tracking-wide"
                >
                  {business.workArea}
                </Badge>
              ) : null}
            </div>
          </div>

          <div className="mt-auto pt-1 flex items-center justify-between gap-2">
            {isEditable && editPath ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 shrink-0 gap-1.5 rounded-full border-primary/40 px-2.5 text-xs font-bold text-primary bg-primary/5 hover:bg-primary/10"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(editPath, { state: { initialData: business } });
                }}
              >
                <Pencil className="h-3.5 w-3.5" />
                Edit
              </Button>
            ) : (
              <span />
            )}
            <p className="text-xs font-semibold text-primary flex items-center gap-1 group-hover:gap-1.5 transition-[gap]">
              Know more
              <ArrowRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
            </p>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function PersonalContactCard({
  user,
  isEditable,
  isEditingPersonal,
  isPersonalSaving,
  personalEditorRef,
  onRequestDiscard,
  onStartEdit,
  onSave,
  onDone,
  onSavingChange,
  onDirtyChange,
}: {
  user: PublicUserWithCommunities;
  isEditable: boolean;
  isEditingPersonal: boolean;
  isPersonalSaving: boolean;
  personalEditorRef: React.RefObject<PersonalInfoSectionEditorRef | null>;
  onRequestDiscard: () => void;
  onStartEdit: () => void;
  onSave: () => void;
  onDone: () => void;
  onSavingChange: (saving: boolean) => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const { mutate: mutateUser } = useUser();
  const [visibilitySaving, setVisibilitySaving] = useState<ProfileVisibilityField | null>(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareMode, setShareMode] = useState<ShareMode>('options');
  const publicSocialLinks = (user.socialLinks as Record<string, string> | null | undefined) ?? {};

  const contactName = [user.firstName, user.middleName, user.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  const displayName = contactName || 'Member';
  const contactText = [
    `Name: ${displayName}`,
    `Number: ${user.phone?.trim() || ''}`,
    `Email: ${user.email?.trim() || ''}`,
  ].join('\n');
  const vCard = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `FN:${escapeVCardValue(displayName)}`,
    `N:${escapeVCardValue(user.lastName || '')};${escapeVCardValue(user.firstName || '')};${escapeVCardValue(user.middleName || '')};;`,
    `TEL;TYPE=CELL:${escapeVCardValue(user.phone?.trim() || '')}`,
    `EMAIL;TYPE=INTERNET:${escapeVCardValue(user.email?.trim() || '')}`,
    'END:VCARD',
  ].join('\r\n');
  const showShareVCard = canShareVCard();
  const initials = `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() || '?';

  const handleShareText = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${displayName} on Milora`,
          text: contactText,
        });
        toast.success('Contact shared');
        return;
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
    }

    try {
      await navigator.clipboard.writeText(contactText);
      toast.success('Contact details copied');
    } catch {
      toast.error('Unable to share contact details');
    }
  };

  const handleShareVCard = async () => {
    const result = await shareVCard({
      vCard,
      filename: displayName.replace(/\s+/g, '-'),
      title: `${displayName} on Milora`,
    });

    if (result === 'cancelled') return;
    if (result === 'shared') {
      toast.success('Contact card shared');
      return;
    }
    toast.error('Unable to share contact card');
  };

  const updateVisibility = async (field: ProfileVisibilityField, value: DateVisibility) => {
    setVisibilitySaving(field);
    try {
      await axiosInstance.patch('/api/user', {
        fieldVisibility: { [field]: value },
      });
      await mutateUser();
      toast.success('Visibility updated');
    } catch {
      toast.error('Failed to update visibility');
    } finally {
      setVisibilitySaving(null);
    }
  };

  const visibilityAction = (field: ProfileVisibilityField, label: string) =>
    isEditable ? (
      <DateVisibilityPicker
        value={getFieldVisibility(user, field)}
        onChange={(value) => void updateVisibility(field, value)}
        disabled={visibilitySaving === field}
        aria-label={`${label} visibility`}
      />
    ) : undefined;

  const shareQrActions = (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label="Share contact"
        className="h-9 w-9 rounded-xl bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:bg-primary/90 hover:shadow-primary/40 active:scale-[0.97] transition-all"
        onClick={() => {
          setShareMode('options');
          setShareDialogOpen(true);
        }}
      >
        <Share2 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label="Show profile QR code"
        className="h-9 w-9 rounded-xl border border-border/30 bg-background/60 backdrop-blur-sm shadow-sm hover:bg-primary/10 hover:text-primary hover:border-primary/30 text-muted-foreground transition-all"
        onClick={() => setQrDialogOpen(true)}
      >
        <QrCode className="h-4 w-4" />
      </Button>
    </>
  );

  return (
    <Card className="glass-card border-border/70 rounded-3xl shadow-xl overflow-hidden">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-black font-display tracking-tight">
          Personal & Contact Information
        </CardTitle>
        {isEditable ? (
          <div className="flex items-center gap-1">
            {isEditingPersonal ? (
              <>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={onRequestDiscard}
                  disabled={isPersonalSaving}
                  className="h-8 w-8 rounded-xl text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                >
                  <X className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  onClick={onSave}
                  disabled={isPersonalSaving}
                  className="h-8 w-8 rounded-xl bg-primary hover:bg-primary/90"
                >
                  {isPersonalSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4" />
                  )}
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={onStartEdit}
                className="gap-1.5 rounded-xl h-8 px-3 text-xs font-bold border-primary/40 text-primary bg-primary/5 hover:bg-primary/10 hover:border-primary/60"
              >
                <Pencil className="h-3.5 w-3.5" />
                Edit
              </Button>
            )}
          </div>
        ) : null}
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        {isEditingPersonal ? (
          <div className="col-span-2">
            <PersonalInfoSectionEditor
              ref={personalEditorRef}
              user={user}
              onDone={onDone}
              onSavingChange={onSavingChange}
              onDirtyChange={onDirtyChange}
            />
          </div>
        ) : (
          <>
            {isEditable ? (
              <div className="col-span-1 md:col-span-2 flex flex-col sm:flex-row sm:items-start gap-3">
                <div className="relative w-full sm:w-auto flex justify-center sm:block shrink-0">
                  <div className="flex flex-col items-center gap-2">
                    <Avatar className="h-[4.75rem] w-[4.75rem] rounded-full shadow-sm ring-2 ring-primary/25">
                      <AvatarImage src={user.image || undefined} className="object-cover" />
                      <AvatarFallback className="rounded-full bg-primary text-primary-foreground text-xl font-black font-display">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden sm:flex flex-row items-center gap-1.5">
                      {shareQrActions}
                    </div>
                  </div>
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 sm:hidden flex flex-col items-center gap-1.5">
                    {shareQrActions}
                  </div>
                </div>

                <div className="relative flex-1 min-w-0 rounded-xl border border-border/70 bg-muted/50 dark:bg-muted/30 p-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
                    <NameField label="First Name" value={user.firstName} required />
                    <NameField label="Middle Name" value={user.middleName} />
                    <NameField
                      label="Last Name"
                      value={user.lastName}
                      required
                      className="sm:col-start-1"
                    />
                  </div>
                </div>
              </div>
            ) : null}

            <DetailItem
              label="Email"
              value={user.email}
              icon={<Mail className="h-4 w-4" />}
              action={visibilityAction('email', 'Email')}
            />
            <DetailItem
              label="Phone"
              value={formatDisplayPhone(user.phone)}
              href={phoneTelHref(user.phone)}
              icon={<Phone className="h-4 w-4" />}
              action={visibilityAction('phone', 'Phone')}
            />
            <DetailItem
              label="Gender"
              value={user.gender}
              icon={<Users className="h-4 w-4" />}
              action={visibilityAction('gender', 'Gender')}
            />
            <DetailItem
              label="Blood Group"
              value={user.bloodGroup}
              icon={<Heart className="h-4 w-4" />}
              action={visibilityAction('bloodGroup', 'Blood Group')}
            />
            <DetailItem
              label="Birthday"
              value={user.dob ? new Date(user.dob).toLocaleDateString() : null}
              icon={<Calendar className="h-4 w-4" />}
              action={visibilityAction('dob', 'Birthday')}
            />
            <DetailItem
              label="Anniversary"
              value={
                user.anniversaryDate ? new Date(user.anniversaryDate).toLocaleDateString() : null
              }
              icon={<Calendar className="h-4 w-4" />}
              action={visibilityAction('anniversary', 'Anniversary')}
            />
            {(() => {
              const visibleAddress = formatVisibleAddress(user.address, user, {
                isOwner: isEditable,
              });
              const hasRawAddress = Boolean(
                user.address &&
                (user.address.line1 ||
                  user.address.line2 ||
                  user.address.city ||
                  user.address.state ||
                  user.address.pincode ||
                  user.address.country)
              );

              if (visibleAddress) {
                return (
                  <div className="col-span-1 md:col-span-2 flex items-center gap-3 p-3 rounded-2xl bg-muted/40 dark:bg-muted/25 border border-border">
                    <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
                      <MapPin className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground leading-none mb-1">
                        Residential Address
                      </p>
                      {visibleAddress.mapLink ? (
                        <a
                          href={visibleAddress.mapLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-bold text-foreground truncate hover:text-primary transition-colors block"
                        >
                          {visibleAddress.text}
                        </a>
                      ) : (
                        <p className="text-sm font-bold text-foreground truncate">
                          {visibleAddress.text}
                        </p>
                      )}
                    </div>
                    {visibilityAction('address', 'Address')}
                  </div>
                );
              }

              if (isEditable) {
                return (
                  <div className="col-span-1 md:col-span-2 flex items-center gap-3 p-3 rounded-2xl bg-muted/40 dark:bg-muted/25 border border-border">
                    <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
                      <MapPin className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground leading-none mb-1">
                        Residential Address
                      </p>
                      <p className="text-sm font-bold text-muted-foreground truncate">
                        {hasRawAddress ? 'Hidden by visibility' : 'Not set'}
                      </p>
                    </div>
                    {visibilityAction('address', 'Address')}
                  </div>
                );
              }

              return null;
            })()}

            {Object.keys(publicSocialLinks).length > 0 ? (
              <div className="col-span-1 md:col-span-2 flex items-start gap-3 p-3 rounded-2xl bg-muted/40 dark:bg-muted/25 border border-border">
                <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0 mt-0.5">
                  <Share2 className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1 space-y-2">
                  <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground leading-none">
                    Social Media
                  </p>
                  <SocialLinksCircleEditor
                    value={publicSocialLinks}
                    onChange={async () => {}}
                    readOnly
                    size="sm"
                  />
                </div>
              </div>
            ) : null}
          </>
        )}
      </CardContent>

      {isEditable ? (
        <>
          <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
            <DialogContent className="max-w-sm rounded-2xl">
              <DialogHeader className="text-center sm:text-center">
                <DialogTitle>Your profile QR code</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col items-center gap-4 py-2">
                <div className="rounded-2xl border border-border bg-white p-4 shadow-sm">
                  <QRCodeSVG value={`M:${user.id}`} size={220} level="H" />
                </div>
                <p className="text-center text-sm text-muted-foreground max-w-xs">
                  Show this code at events for quick check-in or walk-in registration.
                </p>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog
            open={shareDialogOpen}
            onOpenChange={(open) => {
              setShareDialogOpen(open);
              if (!open) setShareMode('options');
            }}
          >
            <DialogContent className="max-w-md rounded-2xl border-border p-6 shadow-xl">
              <DialogHeader className="text-left sm:text-left">
                <DialogTitle className="text-xl font-bold tracking-tight text-foreground">
                  Share contact
                </DialogTitle>
                <p className="text-sm text-muted-foreground">
                  Choose how to share {displayName}&apos;s details.
                </p>
              </DialogHeader>

              <div className="space-y-4">
                {shareMode === 'options' ? (
                  <div className="space-y-2">
                    {showShareVCard ? (
                      <button
                        type="button"
                        className="group flex w-full items-center gap-3 rounded-xl border border-border bg-background p-3 text-left transition-colors hover:border-primary/40 hover:bg-primary/[0.04]"
                        onClick={() => void handleShareVCard()}
                      >
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                          <FileUser className="h-5 w-5" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block font-bold text-foreground">Share vCard</span>
                          <span className="mt-0.5 block text-xs text-muted-foreground">
                            Save as a contact file
                          </span>
                        </span>
                        <ArrowLeft className="h-4 w-4 rotate-180 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="group flex w-full items-center gap-3 rounded-xl border border-border bg-background p-3 text-left transition-colors hover:border-primary/40 hover:bg-primary/[0.04]"
                      onClick={() => void handleShareText()}
                    >
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Clipboard className="h-5 w-5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block font-bold text-foreground">Share as text</span>
                        <span className="mt-0.5 block text-xs text-muted-foreground">
                          Send in a message or email
                        </span>
                      </span>
                      <ArrowLeft className="h-4 w-4 rotate-180 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                    </button>
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      className="flex items-center gap-1 text-xs font-bold text-muted-foreground transition-colors hover:text-primary"
                      onClick={() => setShareMode('options')}
                    >
                      <ArrowLeft className="h-3.5 w-3.5" />
                      Choose another format
                    </button>
                    <div className="overflow-hidden rounded-xl border border-border bg-muted/30">
                      <div className="flex items-center gap-3 border-b border-border bg-background px-4 py-3">
                        <Avatar className="h-10 w-10 rounded-xl ring-2 ring-primary/10">
                          <AvatarImage src={user.image || undefined} className="object-cover" />
                          <AvatarFallback className="bg-primary/10 font-black text-primary">
                            {initials}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-black text-foreground">
                            {displayName}
                          </p>
                          <p className="text-[10px] font-bold uppercase tracking-widest text-primary">
                            {shareMode === 'vcard' ? 'Virtual contact card' : 'Contact details'}
                          </p>
                        </div>
                      </div>
                      <pre className="whitespace-pre-wrap px-4 py-3 font-mono text-xs leading-6 text-foreground/80">
                        {shareMode === 'vcard' ? vCard : contactText}
                      </pre>
                    </div>
                    <Button
                      type="button"
                      className="h-10 w-full gap-2 rounded-lg bg-primary font-bold text-primary-foreground shadow-md shadow-primary/20 hover:bg-primary/90"
                      onClick={() =>
                        void (shareMode === 'vcard' ? handleShareVCard() : handleShareText())
                      }
                    >
                      <Share2 className="h-4 w-4" />
                      {shareMode === 'vcard' ? 'Share vCard file' : 'Share text details'}
                    </Button>
                  </>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </>
      ) : null}
    </Card>
  );
}

function NameField({
  label,
  value,
  required,
  className,
}: {
  label: string;
  value?: string | null;
  required?: boolean;
  className?: string;
}) {
  return (
    <div className={cn('space-y-2', className)}>
      <p className="text-sm font-medium leading-none">
        {label}
        {required ? <span className="text-primary"> *</span> : null}
      </p>
      <div className="flex h-10 w-full items-center rounded-xl border border-border/60 bg-transparent px-3 text-sm font-semibold text-foreground">
        <span className="truncate">{value?.trim() || '—'}</span>
      </div>
    </div>
  );
}

function DetailItem({
  label,
  value,
  icon,
  href,
  action,
}: {
  label: string;
  value?: string | null;
  icon: React.ReactNode;
  href?: string;
  action?: React.ReactNode;
}) {
  if (!value && !action) return null;
  const valueNode = value ? (
    href ? (
      <a
        href={href}
        className="text-sm font-bold text-foreground truncate hover:text-primary transition-colors"
      >
        {value}
      </a>
    ) : (
      <p className="text-sm font-bold text-foreground truncate">{value}</p>
    )
  ) : (
    <p className="text-sm font-bold text-muted-foreground truncate">Not set</p>
  );
  return (
    <div className="flex items-center gap-3 p-3 rounded-2xl bg-muted/40 dark:bg-muted/25 border border-border">
      <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground leading-none mb-1">
          {label}
        </p>
        {valueNode}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}
